// --- src/pages/UserDashboard.jsx ---
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Server, Activity, Database, LogOut, Settings, Package,
  RefreshCw, FileText, X, AlertCircle
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import HomeButton from '../components/HomeButton';

// ─── Logs modal ────────────────────────────────────────────────────────────
const LogsModal = ({ appName, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchLogs = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/user/deployments/${encodeURIComponent(appName)}/logs`);
      setLogs(res.data.logs || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appName]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Logs</h3>
            <p className="text-xs text-slate-400 font-mono">{appName}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchLogs}
              disabled={loading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto bg-black/40 border border-slate-800 rounded-lg p-4 font-mono text-xs">
          {loading && logs.length === 0 && (
            <p className="text-slate-500">Loading logs...</p>
          )}
          {error && (
            <div className="flex items-start gap-2 text-red-400">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <p>{error}</p>
            </div>
          )}
          {!error && !loading && logs.length === 0 && (
            <p className="text-slate-500">No log output available.</p>
          )}
          {logs.map((line, i) => (
            <div key={i} className="text-slate-300 whitespace-pre-wrap break-all leading-relaxed">
              {line}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const UserDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalDeployments: 0,
    activeServers: 0,
    uptime: 0
  });
  const [deployments, setDeployments] = useState([]);
  const [groupedByNamespace, setGroupedByNamespace] = useState({});
  const [namespaces, setNamespaces] = useState([]);
  const [selectedNamespace, setSelectedNamespace] = useState('all');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // ── Sync / logs UI state ──────────────────────────────────────────────
  const [syncingApp, setSyncingApp] = useState(null); // app name currently syncing
  const [syncError, setSyncError] = useState('');
  const [logsAppName, setLogsAppName] = useState(null); // app name shown in logs modal, or null

  // Get user's full name
  const getUserFullName = () => {
    if (!user) return 'User';
    const firstName = user.firstName || user.first_name || '';
    const lastName = user.lastName || user.last_name || '';
    return `${firstName} ${lastName}`.trim() || user.email || 'User';
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoadError('');
    try {
      // NOTE: no leading "/api" here — the `api` service's baseURL already
      // includes it (see other dashboards: '/admin/stats', '/user/stats',
      // '/monitoring/services').
      const [statsRes, deploymentsRes] = await Promise.all([
        api.get('/user/stats'),        // User-specific stats endpoint
        api.get('/user/deployments')   // User-specific deployments endpoint
      ]);

      console.log('Stats:', statsRes.data);
      console.log('Deployments:', deploymentsRes.data);

      setStats(statsRes.data);

      // Handle both old and new response formats
      const deploymentsData = deploymentsRes.data.deployments || deploymentsRes.data;
      const groupedData = deploymentsRes.data.groupedByNamespace || {};
      const namespacesData = deploymentsRes.data.namespaces || [];

      setDeployments(Array.isArray(deploymentsData) ? deploymentsData : []);
      setGroupedByNamespace(groupedData);
      setNamespaces(namespacesData);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setLoadError(
        error.response?.data?.message ||
        'Could not load your deployments. Please try refreshing the page.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSync = async (appName) => {
    setSyncingApp(appName);
    setSyncError('');
    try {
      await api.post(`/user/deployments/${encodeURIComponent(appName)}/sync`);
      // Give ArgoCD a moment to pick up the sync before refreshing status
      setTimeout(() => {
        fetchDashboardData();
      }, 1500);
    } catch (error) {
      console.error('Error syncing app:', error);
      setSyncError(error.response?.data?.message || `Failed to sync ${appName}`);
    } finally {
      setSyncingApp(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'healthy':
        return 'bg-green-500/10 text-green-400 border border-green-500/20';
      case 'degraded':
        return 'bg-red-500/10 text-red-400 border border-red-500/20';
      case 'progressing':
        return 'bg-blue-500/10 text-blue-400 border border-blue-500/20';
      case 'missing':
        return 'bg-orange-500/10 text-orange-400 border border-orange-500/20';
      case 'unknown':
        return 'bg-slate-500/10 text-slate-400 border border-slate-500/20';
      default:
        return 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20';
    }
  };

  const getSyncStatusColor = (syncStatus) => {
    switch (syncStatus?.toLowerCase()) {
      case 'synced':
        return 'bg-green-500/10 text-green-400 border border-green-500/20';
      case 'outofsync':
        return 'bg-orange-500/10 text-orange-400 border border-orange-500/20';
      case 'unknown':
        return 'bg-slate-500/10 text-slate-400 border border-slate-500/20';
      default:
        return 'bg-gray-500/10 text-gray-400 border border-gray-500/20';
    }
  };

  const filteredDeployments = selectedNamespace === 'all' 
    ? deployments 
    : groupedByNamespace[selectedNamespace] || [];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-white">Loading dashboard...</div>
      </div>
    );
  }

  return (

    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
          <HomeButton />
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-950/80 backdrop-blur-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-lg flex items-center justify-center">
                <Server className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold">KaydSystems Dashboard</h1>
                <p className="text-xs text-slate-400">Welcome back, {getUserFullName()}</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button className="p-2 text-slate-400 hover:text-white transition-colors">
                <Settings className="w-5 h-5" />
              </button>
              <button 
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <LogOut className="w-4 h-4" />
                <span>Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {loadError && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm">{loadError}</p>
          </div>
        )}
        {syncError && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center justify-between">
            <p className="text-red-400 text-sm">{syncError}</p>
            <button onClick={() => setSyncError('')} className="text-red-400 hover:text-red-300">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Stats Grid - Only 3 cards for regular users (NO Total Users) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-blue-500/10 rounded-lg flex items-center justify-center">
                <Server className="w-6 h-6 text-blue-400" />
              </div>
              <span className="text-2xl font-bold">{stats.totalDeployments}</span>
            </div>
            <h3 className="text-slate-400 text-sm">My Deployments</h3>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-500/10 rounded-lg flex items-center justify-center">
                <Activity className="w-6 h-6 text-green-400" />
              </div>
              <span className="text-2xl font-bold">{stats.activeServers}</span>
            </div>
            <h3 className="text-slate-400 text-sm">Active Services</h3>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-500/10 rounded-lg flex items-center justify-center">
                <Database className="w-6 h-6 text-purple-400" />
              </div>
              <span className="text-2xl font-bold">
                {stats.activeServers === 0 ? 'None' : `${stats.uptime}%`}
              </span>
            </div>
            <h3 className="text-slate-400 text-sm">System Uptime</h3>
          </div>
        </div>

        {/* My Deployments */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold">My Deployments</h2>
            
            {/* Namespace Filter */}
            {namespaces.length > 0 && (
              <div className="flex items-center space-x-2">
                <Package className="w-4 h-4 text-slate-400" />
                <select
                  value={selectedNamespace}
                  onChange={(e) => setSelectedNamespace(e.target.value)}
                  className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Namespaces ({deployments.length})</option>
                  {namespaces.map((ns) => (
                    <option key={ns} value={ns}>
                      {ns} ({groupedByNamespace[ns]?.length || 0})
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left border-b border-slate-800">
                  <th className="pb-3 text-slate-400 font-medium">Application</th>
                  <th className="pb-3 text-slate-400 font-medium">Namespace</th>
                  <th className="pb-3 text-slate-400 font-medium">Health</th>
                  <th className="pb-3 text-slate-400 font-medium">Sync Status</th>
                  <th className="pb-3 text-slate-400 font-medium">Date</th>
                  <th className="pb-3 text-slate-400 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDeployments.length > 0 ? (
                  filteredDeployments.map((deployment) => (
                    <tr key={deployment.id} className="border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors">
                      <td className="py-4 font-medium">{deployment.name}</td>
                      <td className="py-4">
                        <span className="px-2 py-1 bg-slate-800 text-slate-300 rounded text-xs font-mono">
                          {deployment.namespace}
                        </span>
                      </td>
                      <td className="py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(deployment.status)}`}>
                          {deployment.status}
                        </span>
                      </td>
                      <td className="py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSyncStatusColor(deployment.syncStatus)}`}>
                          {deployment.syncStatus}
                        </span>
                      </td>
                      <td className="py-4 text-slate-400">{deployment.date}</td>
                      <td className="py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleSync(deployment.name)}
                            disabled={syncingApp === deployment.name}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-blue-500/10 text-blue-400 border border-blue-500/20 hover:bg-blue-500/20 rounded-lg transition-colors disabled:opacity-50"
                          >
                            <RefreshCw className={`w-3.5 h-3.5 ${syncingApp === deployment.name ? 'animate-spin' : ''}`} />
                            {syncingApp === deployment.name ? 'Syncing...' : 'Sync'}
                          </button>
                          <button
                            onClick={() => setLogsAppName(deployment.name)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-slate-800 text-slate-300 border border-slate-700 hover:bg-slate-700 rounded-lg transition-colors"
                          >
                            <FileText className="w-3.5 h-3.5" />
                            Logs
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-slate-400">
                      {selectedNamespace === 'all' 
                        ? 'No deployments yet' 
                        : `No deployments in ${selectedNamespace} namespace`}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Namespace Summary Cards */}
        {namespaces.length > 1 && (
          <div className="mt-8">
            <h2 className="text-2xl font-bold mb-4">My Namespaces</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {namespaces.map((ns) => {
                const apps = groupedByNamespace[ns] || [];
                const healthyCount = apps.filter(app => app.status === 'Healthy').length;
                const syncedCount = apps.filter(app => app.syncStatus === 'Synced').length;
                
                return (
                  <div 
                    key={ns}
                    className="bg-slate-900 border border-slate-800 rounded-xl p-4 hover:border-slate-700 transition-colors cursor-pointer"
                    onClick={() => setSelectedNamespace(ns)}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-semibold text-lg">{ns}</h3>
                      <span className="text-2xl font-bold text-blue-400">{apps.length}</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between text-slate-400">
                        <span>Healthy:</span>
                        <span className="text-green-400">{healthyCount}/{apps.length}</span>
                      </div>
                      <div className="flex justify-between text-slate-400">
                        <span>Synced:</span>
                        <span className="text-green-400">{syncedCount}/{apps.length}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-8 bg-gradient-to-br from-blue-500/10 to-cyan-400/10 border border-slate-800 rounded-xl p-6">
          <h3 className="text-lg font-semibold text-white mb-2">Need Help?</h3>
          <p className="text-slate-400 mb-4">
            Contact our support team if you need assistance with your deployments.
          </p>
           <a href="/contact">
          <button className="px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors">
            Contact Support
          </button>
          </a>
        </div>
      </main>

      {logsAppName && (
        <LogsModal appName={logsAppName} onClose={() => setLogsAppName(null)} />
      )}
    </div>
  );
};

export default UserDashboard;